import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getSupabase } from '../api/supabase'
import type { CurrentUser } from '../types/profile'
import { BUSINESS_ROLES, type BusinessRole } from '../types/role'

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

export type UseCurrentUserResult = {
  user: CurrentUser | undefined
  status: AuthStatus
  signOut: () => Promise<void>
}

export function useCurrentUser(): UseCurrentUserResult {
  const [hasSession, setHasSession] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    const sb = getSupabase()
    let mounted = true
    void sb.auth.getSession().then(({ data }) => {
      if (mounted) setHasSession(Boolean(data.session))
    })
    const { data: sub } = sb.auth.onAuthStateChange((_event, session) => {
      if (mounted) setHasSession(Boolean(session))
    })
    return () => {
      mounted = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const { data: user } = useQuery({
    queryKey: ['auth', 'current-user'] as const,
    queryFn: async (): Promise<CurrentUser | null> => {
      const sb = getSupabase()
      const { data: authData } = await sb.auth.getUser()
      const authUser = authData.user
      if (!authUser) return null

      // PostgREST 查业务 profile（RLS 限制只返自己）+ user_roles
      // 2026-05-20:A-31 profiles.agent_id → first_agent_id 重命名后同步
      // eslint-disable-next-line no-restricted-syntax -- 读自己的 profile(by auth.uid;owner RLS)
      const { data: profileRow } = await sb
        .from('profiles')
        .select('user_id, first_agent_id, username, email_real, avatar_url, invited_by, created_at')
        .eq('user_id', authUser.id)
        .maybeSingle()

      // user_roles 基表对 authenticated REVOKE ALL(防作弊);读自己角色走 definer 自身视图 v_user_roles_self(auth.uid() 收口)
      // eslint-disable-next-line no-restricted-syntax -- 读自己角色(v_user_roles_self;视图内 auth.uid() 过滤)
      const { data: rolesRows } = await sb
        .from('v_user_roles_self')
        .select('role')
        .eq('user_id', authUser.id)

      return {
        user_id: authUser.id,
        // DB 字段为 first_agent_id(A-31);TS CurrentUser 保持 agent_id 名(consumer 多)
        agent_id: (profileRow?.first_agent_id as 'default') ?? 'default',
        username: profileRow?.username ?? authUser.email ?? '',
        email: profileRow?.email_real ?? '',
        avatar_url: profileRow?.avatar_url ?? null,
        invited_by: profileRow?.invited_by ?? null,
        user_level: 0,
        vip_tier: 0,
        vip_expires_at: null,
        created_at: profileRow?.created_at ?? new Date().toISOString(),
        roles: ((rolesRows ?? []) as Array<{ role: string }>)
          .map((r) => r.role)
          .filter((r): r is BusinessRole => (BUSINESS_ROLES as readonly string[]).includes(r)),
      }
    },
    enabled: hasSession === true,
    staleTime: 60_000,
  })

  const status: AuthStatus =
    hasSession === undefined ? 'loading' : hasSession ? 'authenticated' : 'unauthenticated'

  const signOut = async () => {
    await getSupabase().auth.signOut()
  }

  return { user: user ?? undefined, status, signOut }
}
